import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Tabs,
  Tab,
  ButtonGroup,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  TablePagination,
  Snackbar,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from "date-fns";
import {
  partnerDashboardService,
  PartnerTransaction,
  PartnerEarnings,
} from "../../../apis/partnerDashboard";

interface PartnerTransactionsProps {
  partnerId: string;
}

const filterTypes = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom Range" },
];

const PartnerTransactions: React.FC<PartnerTransactionsProps> = ({ partnerId }) => {
  const [transactions, setTransactions] = useState<PartnerTransaction[]>([]);
  const [earnings, setEarnings] = useState<PartnerEarnings | null>(null);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("daily");
  const [startDate, setStartDate] = useState<Date | null>(startOfDay(new Date()));
  const [endDate, setEndDate] = useState<Date | null>(endOfDay(new Date()));
  const [driverTypeTab, setDriverTypeTab] = useState(0); // 0: All, 1: Internal, 2: External
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  useEffect(() => {
    fetchTransactions();
    fetchEarnings();
  }, [partnerId, filterType, startDate, endDate, driverTypeTab, page, rowsPerPage]);

  const updateDateRange = (type: string) => {
    const today = new Date();
    switch (type) {
      case "daily":
        setStartDate(startOfDay(today));
        setEndDate(endOfDay(today));
        break;
      case "weekly":
        setStartDate(startOfWeek(today));
        setEndDate(endOfWeek(today));
        break;
      case "monthly":
        setStartDate(startOfMonth(today));
        setEndDate(endOfMonth(today));
        break;
    }
    setFilterType(type);
  };

  const fetchTransactions = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const filters: any = {
        page: page + 1,
        limit: rowsPerPage,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        filter_type: filterType,
      };
      if (driverTypeTab === 1) filters.is_internal = true;
      if (driverTypeTab === 2) filters.is_internal = false;

      const data = await partnerDashboardService.getTransactions(partnerId, filters);
      setTransactions(data.transactions);
      setTotal(data.pagination.total);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch transactions",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    if (!startDate || !endDate) return;
    try {
      const data = await partnerDashboardService.getEarnings(partnerId, {
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        filter_type: filterType,
      });
      setEarnings(data);
    } catch (error) {
      console.error("Failed to fetch earnings:", error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDriverTypeTab(newValue);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Transactions & Earnings
      </Typography>

      {/* Earnings Summary Cards */}
      {earnings && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Commission
                </Typography>
                <Typography variant="h4">${earnings.total_commission.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Rides
                </Typography>
                <Typography variant="h4">{earnings.total_rides}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Fare
                </Typography>
                <Typography variant="h4">${earnings.total_fare.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <ButtonGroup variant="outlined">
          {filterTypes.map((type) => (
            <Button
              key={type.value}
              variant={filterType === type.value ? "contained" : "outlined"}
              onClick={() => updateDateRange(type.value)}
            >
              {type.label}
            </Button>
          ))}
        </ButtonGroup>

        {filterType === "custom" && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { size: "small" } }}
            />
          </LocalizationProvider>
        )}
      </Box>

      {/* Driver Type Tabs */}
      <Tabs value={driverTypeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="All Drivers" />
        <Tab label="Internal Drivers" />
        <Tab label="External Drivers" />
      </Tabs>

      {/* Transactions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Driver Name</TableCell>
              <TableCell>Pickup Location</TableCell>
              <TableCell>Dropoff Location</TableCell>
              <TableCell>Pickup Time</TableCell>
              <TableCell>Dropoff Time</TableCell>
              <TableCell>Ride Amount</TableCell>
              <TableCell>Partner Commission</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No transactions found
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((transaction) => (
                <TableRow key={transaction._id}>
                  <TableCell>{transaction.driver?.driver_name || "N/A"}</TableCell>
                  <TableCell>{transaction.pickup_location?.address || "N/A"}</TableCell>
                  <TableCell>{transaction.destination_location?.address || "N/A"}</TableCell>
                  <TableCell>{transaction.start_time ? format(new Date(transaction.start_time), "PPp") : "N/A"}</TableCell>
                  <TableCell>{transaction.end_time ? format(new Date(transaction.end_time), "PPp") : "N/A"}</TableCell>
                  <TableCell>${transaction.fare_amount?.toFixed(2) || "0.00"}</TableCell>
                  <TableCell>${transaction.partner_commission?.toFixed(2) || "0.00"}</TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.ride_status}
                      color={transaction.ride_status === "COMPLETED" ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerTransactions;

